<template>
  <div>
    <!-- models select -->
    <b-row no-gutters class="mt-3 mb-3" v-if="getModelsPermission">
      <b-col>
        <div class="d-flex flex-row p-3 bg-white scoring-model-select">
          <div class="multiselect-width">
            <label for="name" v-t="'scoringProfileDetails.associateModelLabel'"></label>
            <custom-multi-select
              maintain-original-order
              v-model="scoringProfileDefinitionSelectValue"
              :options="availableModels"
              :variant="'white'"
              :placeholder="$t('common.search')"
              :default-text="$t('scoringProfileDetails.noAssociatedModel')"
            >
            </custom-multi-select>
          </div>
        </div>
      </b-col>
    </b-row>
    <!-- title -->
    <div>
      <template v-if="isScoringDefinitionCustomConfig">
        <div class="p-3 bg-white">
          <b-row no-gutters class="mb-3">
            <b-col>
              <div>
                <span class="font-weight-bold">
                  {{ $t('scoringProfiles.scoringDefinitionWeights') }}
                </span>
              </div>
            </b-col>
          </b-row>

          <!-- select -->
          <b-row no-gutters class="mb-3">
            <b-col>
              <div class="d-flex flex-row">
                <div class="w-25">
                  <simple-select
                    :select-size="'sm'"
                    :class="['bg-white']"
                    :options="options"
                    :selected="getSelectedWeightType"
                    @emit-selected="onChangeWeights"
                  ></simple-select>
                </div>
                <div class="ml-4" v-if="getSelectedScoringDefinition.guideline">
                  <span class="text-muted">
                    {{ $t('scoringProfiles.customDriverGuideline') }}
                  </span>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- slider -->
          <b-row no-gutters v-if="getSelectedScoringDefinition.slider">
            <b-col>
              <div class="mb-1">
                <custom-slider
                  :value.sync="geometricDriversSequenceWeightsDecay"
                  :min="0"
                  :max="1"
                  :interval="0.01"
                  :process="false"
                  :tooltip="'none'"
                  :direction="'rtl'"
                ></custom-slider>
              </div>
              <div class="d-flex flex-row">
                <div>
                  <span class="slider-bottom-label">{{ $t('scoringProfiles.label.equallyWeighted') }}</span>
                  <custom-tooltip
                    :index="0"
                    :css-class="['ml-2 pointer']"
                    :section="'scoring-definition'"
                    :tooltipText="$t('scoringProfiles.tips.equallyWeighted')"
                  ></custom-tooltip>
                </div>
                <div class="ml-auto">
                  <span class="slider-bottom-label">{{ $t('scoringProfiles.label.skewed') }}</span>
                  <custom-tooltip
                    :index="1"
                    :css-class="['ml-2 pointer']"
                    :section="'scoring-definition'"
                    :tooltipText="$t('scoringProfiles.tips.skewed')"
                  ></custom-tooltip>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- separator -->
          <b-row no-gutters>
            <b-col>
              <hr />
            </b-col>
          </b-row>

          <!-- guideline -->
          <b-row no-gutters class="mb-3">
            <b-col>
              <span class="user-guideline-text">
                {{ userGuidelineText }}
              </span>
            </b-col>
          </b-row>

          <!-- validation messages -->
          <b-row
            no-gutters
            :class="getDriversWeightsGreaterThanZero ? 'mb-3' : 'mb-0'"
            v-if="getSelectedScoringDefinition.list.length > 0 && !isValidMaxTotalWeight"
          >
            <b-col>
              <div class="text-danger">
                <font-awesome-icon class="small" icon="exclamation-triangle" />
                <span>{{ $t('scoringProfiles.currentWeight') }}: </span>
                <span class="font-weight-bold">{{ $n(getMaxTotalWeight, 'percent') }}.</span>
                <span class="ml-1" v-html="$t('scoringProfiles.maxWeight')"></span>
              </div>
            </b-col>
          </b-row>

          <b-row
            no-gutters
            class="mb-3"
            v-if="getSelectedScoringDefinition.list.length > 0 && !getDriversWeightsGreaterThanZero"
          >
            <b-col>
              <div class="text-danger">
                <font-awesome-icon class="small mr-1" icon="exclamation-circle" />
                <span v-html="$t('scoringProfiles.weightsMustBeGreaterThan0')"></span>
              </div>
            </b-col>
          </b-row>

          <!-- scoring definition component -->
          <b-row no-gutters class="mb-3" v-if="isScoringDefinitionCustomConfig">
            <b-col>
              <scoring-profiles-definition
                :indicators="getDriversIndicators"
                :drivers-limit="maxDriversListAccepted"
                :drivers="getSelectedScoringDefinition.list"
                :weight-type="getSelectedScoringDefinition.config"
                :can-edit-badges="getSelectedScoringDefinition.edit"
                :sequence-weights-decay="getSequenceWeightsDecay"
                :indicators-categories="getDriversIndicatorsCategories"
                @on-add-driver="onAddDriver"
                @on-remove-all="onRemoveAll"
                @on-remove-driver="onRemoveDriver"
                @on-update-custom-driver-weight="onUpdateCustomDriverWeight"
                @on-update-drivers-list-sorting="onUpdateDriversListSorting"
              ></scoring-profiles-definition>
            </b-col>
          </b-row>
        </div>
      </template>

      <!-- separator -->
      <b-row no-gutters>
        <b-col>
          <div class="border-bottom mb-3"></div>
        </b-col>
      </b-row>

      <template>
        <div class="p-3 bg-white" v-if="getModelsPermission && getSelectedModelId">
          <b-row no-gutters class="mb-3">
            <b-col>
              <span class="user-guideline-text">
                {{ $t('scoringProfiles.dragModelDriversGuideline') }}
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <scoring-profiles-model-definition
                :driverFamiliesList="getUnsortedModelDrivers"
                :drivers="getSortedModelDrivers"
                :drivers-limit="maxDriversListAccepted"
                @on-move-driver="onMoveDriver"
                @on-reset-sorting="onResetSorting"
                @on-update-sorting="onUpdateSorting"
                @on-remove-model-driver="onRemoveModelDriver"
              >
                <template>
                  <div class="text-right">
                    <b-button
                      @click="onSuggestedSorting"
                      variant="link"
                      class="sorting-btn"
                      size="sm"
                      :disabled="suggestedSortingRunning"
                    >
                      {{ $t('scoringProfileDetails.suggestedSorting') }}
                      <b-spinner variant="primary" small v-if="suggestedSortingRunning"></b-spinner>
                      <font-awesome-icon v-else :icon="['far', 'lightbulb']" />
                    </b-button>
                  </div>
                </template>
              </scoring-profiles-model-definition>
            </b-col>
          </b-row>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CustomTooltip from '../../components/Lib/CustomTooltip.vue'
import SimpleSelect from '../../components/Lib/SimpleSelect.vue'
import CustomSlider from '../../components/Lib/CustomSlider.vue'
import CustomMultiSelect from '../../components/Lib/CustomMultiSelect.vue'
import ScoringProfilesDefinition from '../../components/ScoringProfiles/ScoringProfilesDefinition.vue'
import ScoringProfilesModelDefinition from '../../components/ScoringProfiles/ScoringProfilesModelDefinition.vue'
import {
  calculateDriversWeightsLists,
  mapScoringDefinitionforModel,
  formatDriverWeightNumber,
} from '../../utils/drivers'
import { SCORING_DEFINITION_TYPES } from '../../settings/scoringProfilesSettings'
import { isNearlyEqual } from '../../utils/formatter'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapMutations, mapActions } = createNamespacedHelpers('scoringProfilesDefinition')

export default {
  components: {
    CustomTooltip,
    CustomSlider,
    SimpleSelect,
    CustomMultiSelect,
    ScoringProfilesDefinition,
    ScoringProfilesModelDefinition,
  },

  data() {
    return {
      maxDriversListAccepted: 30,
      suggestedSortingRunning: false,
    }
  },

  computed: {
    ...mapGetters([
      'getMaxTotalWeight',
      'getSelectedModelId',
      'getDriversIndicators',
      'getSelectedWeightType',
      'getSortedModelDrivers',
      'getSortedModelWeights',
      'getUnsortedModelDrivers',
      'getSequenceWeightsDecay',
      'getScoringDefinitionSettings',
      'getSelectedScoringDefinition',
      'getDriversIndicatorsCategories',
      'getDriversWeightsGreaterThanZero',
      'getScoringProfileDefinitionSelectValue',
    ]),

    isScoringDefinitionCustomConfig: {
      get() {
        return this.getScoringProfileDefinitionSelectValue === 0
      },
    },

    scoringProfileDefinitionSelectValue: {
      get() {
        return this.getScoringProfileDefinitionSelectValue == null
          ? [-1]
          : [this.getScoringProfileDefinitionSelectValue.toString()]
      },
      set(newVal) {
        this.setScoringProfileDefinitionSelectValue(parseInt(newVal[0]))
        if (newVal[0] && newVal[0] !== 0) {
          this.onChangeModelType(parseInt(newVal[0]))
        }
      },
    },

    options: {
      get() {
        return [
          { value: 1, text: this.$t('scoringProfiles.geometricOption') },
          { value: 2, text: this.$t('scoringProfiles.customOption') },
          { value: 3, text: this.$t('scoringProfiles.linearOption') },
        ]
      },
    },

    getModelsPermission: {
      get() {
        return this.$store.getters['can_ViewScoringProfileModels']
      },
    },

    availableModels: {
      get() {
        const modelsList = this.$store.getters['scoringProfilesModelsList/getModelsListToAssociate']
        let multiSelectOptions = [{ value: '0', text: this.$t('scoringProfileDetails.customDefinition') }]
        modelsList.forEach((el) => {
          multiSelectOptions.push({ value: el.id.toString(), text: el.name })
        })
        return multiSelectOptions
      },
    },

    userGuidelineText: {
      get() {
        return this.getSelectedWeightType === 2
          ? this.$t('scoringProfiles.dragDriversCustomGuideline')
          : this.$t('scoringProfiles.dragDriversGuideline')
      },
    },

    geometricDriversSequenceWeightsDecay: {
      get() {
        return this.getSequenceWeightsDecay
      },
      set(newVal) {
        this.setSequenceWeightsDecay(newVal)
        let list = [...this.getSelectedScoringDefinition.list]
        calculateDriversWeightsLists(
          null,
          list,
          this.getSelectedScoringDefinition.config,
          this.getSequenceWeightsDecay,
          this.setDriversList
        )
      },
    },

    isValidMaxTotalWeight: {
      get() {
        const maxWeight = this.getMaxTotalWeight
        const epsilon = this.getSelectedScoringDefinition.config === SCORING_DEFINITION_TYPES.custom ? 0.0001 : 0.0005
        return isNearlyEqual(maxWeight, 1, epsilon)
      },
    },
  },

  methods: {
    ...mapActions(['loadDriversIndicators', 'resetSelectedModel', 'suggestedSorting']),
    ...mapMutations([
      'setSelectedWeightType',
      'setSequenceWeightsDecay',
      'setDriversList',
      'setSelectedModelId',
      'setSortedModelDrivers',
      'setUnsortedModelDrivers',
      'setSortedModelWeights',
      'setSelectedModelName',
      'setScoringProfileDefinitionSelectValue',
    ]),

    onChangeModelType(modelId) {
      this.resetSelectedModel()
      this.setSortedModelWeights([])
      const id = modelId === 0 ? null : modelId
      this.setSelectedModelId(id)
      if (!id) return
      this.$store.dispatch('scoringProfilesCommon/setLoading', true)
      this.$store
        .dispatch('scoringProfilesModelsEditing/loadModelForLinking', id)
        .then((modelInfo) => {
          this.setSelectedModelName(modelInfo.name)
          const result = mapScoringDefinitionforModel(modelInfo.scoringDefinition)
          this.setSortedModelWeights(result.sortedWeights)
          this.setUnsortedModelDrivers(result.driversList)
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.$store.dispatch('scoringProfilesCommon/setLoading', false)
        })
    },

    onSuggestedSorting() {
      this.suggestedSortingRunning = true

      const delay = (time) => {
        return new Promise((resolve) => setTimeout(resolve, time))
      }

      delay(1000).then(() => {
        this.suggestedSorting()
          .then((drivers) => {
            let sortedDrivers = []
            const weights = this.getSortedModelWeights
            drivers.forEach((el, i) => {
              sortedDrivers.push({ driver: el, weight: weights[i] })
            })
            this.setSortedModelDrivers(sortedDrivers)
          })
          .catch(async (error) => {
            const err = await error.response
            const message = await err.text()
            if (['NotEnoughData', 'NotUniqueOrder'].includes(message)) {
              this.$bvToast.toast(this.$t('scoringProfileDetails.suggestedSortingError'), {
                title: this.$t('scoringProfiles.scoringDefinition'),
                variant: 'danger',
              })
            }
          })
          .finally(() => {
            this.suggestedSortingRunning = false
          })
      })
    },

    onChangeWeights(val) {
      let sourceList = this.getSelectedScoringDefinition.list
      let targetList = this.getScoringDefinitionSettings[val].list

      if (this.getScoringDefinitionSettings[val].config === SCORING_DEFINITION_TYPES.custom && targetList.length > 0) {
        sourceList.forEach((j) => {
          const obj = targetList.find((k) => k.driver === j.driver)
          j.weight = obj ? obj.weight : j.weight
        })
      }

      this.setSelectedWeightType(val)
      calculateDriversWeightsLists(
        null,
        sourceList,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onRemoveAll() {
      const emptyList = []
      this.setDriversList(emptyList)
    },

    onRemoveDriver(value) {
      const list = [...this.getSelectedScoringDefinition.list].filter((item) => item.driver !== value)
      calculateDriversWeightsLists(
        null,
        list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onAddDriver(value) {
      const list = [...this.getSelectedScoringDefinition.list]
      calculateDriversWeightsLists(
        value,
        list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onUpdateCustomDriverWeight(name, value) {
      let list = [...this.getSelectedScoringDefinition.list]
      const index = list.findIndex((el) => el.driver === name)
      list[index].weight = formatDriverWeightNumber(value)
      this.setDriversList(list)
    },

    onUpdateDriversListSorting(value) {
      if (!Array.isArray(value)) return false
      const list = value.map((k) => {
        return typeof k === 'string' ? { driver: k, weight: 0 } : k
      })
      calculateDriversWeightsLists(
        null,
        list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onMoveDriver(item) {
      let list = this.getSortedModelDrivers
      let weigths = this.getSortedModelWeights
      if (!list.some((el) => el.driver === item.driver)) {
        list.push(item)
        list.map((el, index) => (el.weight = weigths[index]))
        this.setSortedModelDrivers(list)
      }
    },

    onResetSorting() {
      const emptyList = []
      this.setSortedModelDrivers(emptyList)
    },

    onUpdateSorting(value) {
      let weigths = this.getSortedModelWeights
      value.map((el, index) => (el.weight = weigths[index]))
      this.setSortedModelDrivers(value)
    },

    onRemoveModelDriver(item) {
      let list = this.getSortedModelDrivers
      let weigths = this.getSortedModelWeights
      list = list.filter((el) => el.driver !== item.driver)
      list.map((el, index) => (el.weight = weigths[index]))
      this.setSortedModelDrivers(list)
    },
  },

  created() {
    this.loadDriversIndicators()
  },
}
</script>

<style lang="scss" scoped>
.user-guideline-text {
  font-size: 0.9rem;
}

.multiselect-width {
  width: 60%;
}

.sorting-btn {
  font-size: 0.8rem !important;
  text-decoration: none !important;
}
</style>
